/**
 * Scheduling's inbox for expedited-dispense requests (workforce write
 * cutover, packet §16). Finance asked; Scheduling reviews, accepts or
 * declines, schedules, closes. Each answer carries the request's version so
 * two people cannot answer the same request twice without noticing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import BuildingOrClinic, { ClinicFields } from '../../components/emerge/BuildingOrClinic';
// 2026-10-14: the views moved from the buttons above the board to the workspace sidebar (Isaiah Cotto).
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { boardLayoutOf, type BoardLayout } from '../../components/board/kanban/layout';
import { Building2, CalendarDays, PackageCheck, SquareKanban, Table2, Zap } from 'lucide-react';
import { schedulingApi, outcomeOf, shortDate, EXPEDITED_STATE_LABEL, SchedulingScope, InboxItem, SchedulingAction, ExpeditedState } from '../finance/financeApi';
import FacilityOperations from './FacilityOperations';
import SchedulingBoard from './SchedulingBoard';
import AtdQueue from './AtdQueue';
import ProviderCalendar from './ProviderCalendar';
import { atdScope } from './atdApi';
import { useBuildingMeta } from './BuildingEditor';
import '../umed/umed.css';
import '../finance/finance.css';
import './scheduling.css';
import { FindResidentEntry } from '../../components/residentDirectory';

/**
 * Views (Tide phase 2, 2026-10-04): the Scheduling BOARD - the Monday board as
 * a read model, tabs from the server's catalog, each building's plan and
 * commands under its row - is the default; the expedited inbox is the second.
 * The facility table (every building, not only the board's) stays reachable
 * at `?view=facilities`, linked from the board's foot, for scheduling a
 * building the board does not carry. The view rides in the URL so a link and
 * a refresh land where the person was.
 */
type View = 'board' | 'expedited' | 'facilities' | 'atd' | 'calendar';
const isView = (v: string | null): v is View => v === 'board' || v === 'facilities' || v === 'expedited' || v === 'atd' || v === 'calendar';
const TITLE: Record<View, string> = { board: 'Scheduling board', expedited: 'Expedited dispense requests', facilities: 'Facility operations', atd: 'ATD: ready to dispense', calendar: 'Provider calendar' };

/** The Kanban's words for a closure kind ("Closed for scheduling (dropped)"). */
const CLOSURE_WORDS: Record<string, string> = { DROPPED: 'dropped', DO_NOT_SCHEDULE: 'do not schedule' };
const ACTION_LABEL: Record<SchedulingAction, string> = { REVIEW: 'Start review', ACCEPT: 'Accept', DECLINE: 'Decline', SCHEDULE: 'Schedule the visit', CLOSE: 'Close' };
const ACTIONS_FOR: Record<ExpeditedState, SchedulingAction[]> = {
  REQUESTED: ['REVIEW', 'ACCEPT', 'DECLINE'], UNDER_REVIEW: ['ACCEPT', 'DECLINE'], ACCEPTED: ['SCHEDULE', 'CLOSE'], DECLINED: ['CLOSE'], SCHEDULED: ['CLOSE'], CLOSED: [],
};

function RequestCard({ item, canRespond, onDone }: { item: InboxItem & ClinicFields; canRespond: boolean; onDone: (msg: string) => Promise<void> }) {
  const [action, setAction] = useState<SchedulingAction | null>(null);
  const [response, setResponse] = useState('');
  const [date, setDate] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Closed for scheduling (Isaiah Cotto, 2026-10-14): the server refuses SCHEDULE when the request's building is closed
  // (409 BUILDING_CLOSED), so it is not offered there; decline and close stay.
  const closed = item.closure_kind ? (CLOSURE_WORDS[item.closure_kind] || item.closure_kind) : null;
  const actions = ACTIONS_FOR[item.state].filter((a) => !(closed && a === 'SCHEDULE'));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); if (!action) return; setBusy(true); setErr(null);
    try {
      const r = await schedulingApi.respond(item.id, { action, response: response.trim() || undefined, scheduled_for: date || undefined, visit_ref: ref.trim() || undefined, version: item.version });
      if (!r.success || !r.data) { setErr(outcomeOf(r, { wrote: true })); return; }
      await onDone(`${item.resident_name}: ${EXPEDITED_STATE_LABEL[r.data.state]}.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <li className="fn-request" aria-label="Expedited dispense request">
      <strong>{item.resident_name}</strong> · <BuildingOrClinic row={item} fallback="No current building" />
      <div className="um-quiet">{EXPEDITED_STATE_LABEL[item.state]} · asked {shortDate(item.requested_at)}{item.requested_by_name ? ` by ${item.requested_by_name}` : ''}{item.urgency === 'REQUESTED_BY_DATE' && item.requested_by_date ? ` · wanted by ${shortDate(item.requested_by_date)}` : ' · as soon as possible'}</div>
      <p>{item.reason}</p>
      {item.arrangement_summary && <p className="um-quiet">Arrangement: {item.arrangement_summary}</p>}
      <p className="um-quiet">Next visit to this building: {item.provider_next_visit_at ? shortDate(item.provider_next_visit_at) : item.tentative_next_visit ? `${shortDate(item.tentative_next_visit)} (tentative)` : 'not scheduled'}</p>
      {item.scheduling_response && <p className="um-quiet">Answer so far: {item.scheduling_response}</p>}
      {closed && <p className="um-notice" role="note">Closed for scheduling ({closed}): no visit is scheduled at this building. Close the request, with a note for Finance, instead.</p>}
      {canRespond && actions.length > 0 && (
        <form className="um-form" onSubmit={submit} aria-label={`Answer for ${item.resident_name}`}>
          <div className="btns">{actions.map((a) => <button key={a} type="button" className={`um-open${action === a ? ' um-primary' : ''}`} onClick={() => setAction(a)}>{ACTION_LABEL[a]}</button>)}</div>
          {action && (
            <>
              <label><span>{action === 'DECLINE' ? 'Why (Finance reads this)' : 'Note for Finance (optional)'}</span><input value={response} onChange={(e) => setResponse(e.target.value)} maxLength={2000} aria-label="Scheduling response" /></label>
              {action === 'SCHEDULE' && <><label><span>Visit date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Visit date" /></label>
                <label><span>Visit reference (optional)</span><input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={200} aria-label="Visit reference" /></label></>}
              <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || (action === 'DECLINE' && !response.trim()) || (action === 'SCHEDULE' && !date)}>{busy ? 'Recording…' : `Confirm: ${ACTION_LABEL[action]}`}</button></div>
            </>
          )}
          {err && <p className="um-notice stop" role="alert">{err}</p>}
        </form>
      )}
    </li>
  );
}

/**
 * Ruling R5 (2026-10-14): someone refused Scheduling (not on the health-data allowlist, or not a responder) who
 * manages buildings across the enterprise is shown the way to the building screens, which need neither.
 */
function BuildingsDoor() {
  const meta = useBuildingMeta();
  if (!meta || !meta.enterprise) return null;
  return <p className="um-quiet">You can still add and edit buildings: <Link to="/portal/buildings">open Buildings</Link>.</p>;
}

export default function SchedulingInbox() {
  const [scope, setScope] = useState<SchedulingScope | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [state, setState] = useState<ExpeditedState | ''>('');
  const [listErr, setListErr] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const view: View = isView(searchParams.get('view')) ? (searchParams.get('view') as View) : 'board';
  // A view change starts that view clean: the board's tab, filters and open row belong to the board. The sidebar's
  // items are links to the same places, still replacing the history entry as the buttons did.
  const setView = (v: View) => { const next = new URLSearchParams(); if (v !== 'board') next.set('view', v); setSearchParams(next, { replace: true }); setNotice(null); };
  const viewHref = (v: View) => (v === 'board' ? pathname : `${pathname}?view=${v}`);
  /**
   * Kanban | Table from the sidebar. On the board the search, filters and sort carry over and the open row and page do
   * not (what the toolbar switch did); a table tab never rides into the Kanban, where it would read as the table.
   */
  const layoutHref = (l: BoardLayout) => {
    const next = view === 'board' ? new URLSearchParams(searchParams) : new URLSearchParams();
    for (const k of ['view', 'open', 'page', 'layout', 'tab']) next.delete(k);
    if (l === 'table') next.set('layout', 'table');
    const qs = next.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  const load = useCallback(async () => {
    setListErr(null);
    const r = await schedulingApi.inbox(state || null);
    if (r.success && r.data) { setItems(r.data.items); return; }
    // Never `[]` on a failure. An empty array renders "Nothing waiting", which
    // tells Scheduling the queue is clear when the truth is that nobody
    // answered — and expedited dispense is the queue where that matters most.
    setItems(null);
    setListErr(outcomeOf(r));
  }, [state]);

  useEffect(() => {
    (async () => {
      try {
        const r = await schedulingApi.scope();
        if (!r.success || !r.data) { setRefusal(String(r.code || '').replace(/^AUTHZ_/, '') === 'DENY_NO_CAPABILITY' ? 'Your role does not answer expedited-dispense requests. Ask your administrator to ratify a Scheduling role.' : outcomeOf(r)); return; }
        setScope(r.data);
      } catch { setRefusal('Could not reach the server.'); }
    })();
  }, []);
  useEffect(() => { if (scope) load(); }, [scope, load]);

  if (refusal) return <div className="um"><main className="um-main one"><section className="um-card"><h2>Scheduling</h2><p className="um-quiet">{refusal}</p><BuildingsDoor /></section></main></div>;
  if (!scope) return <div className="um"><main className="um-main one"><section className="um-card"><p className="um-quiet">Opening Scheduling…</p></section></main></div>;
  const atd = atdScope(scope);
  const layout = boardLayoutOf(searchParams);
  const blocked = scope.counts && scope.counts.open_blockers ? scope.counts.open_blockers : 0;
  const expedited = scope.counts && scope.counts.expedited_pending ? scope.counts.expedited_pending : 0;
  const clearNotice = () => setNotice(null);
  const sidebarGroups: SidebarGroup[] = [
    {
      key: 'views', label: 'Views',
      items: [
        { key: 'kanban', label: 'Kanban', icon: SquareKanban, href: layoutHref('kanban'), replace: true, active: view === 'board' && layout === 'kanban',
          badge: blocked ? { value: blocked, label: `${blocked} blocked` } : null, title: 'The board as lanes, one per state. A drag never records anything.' },
        { key: 'table', label: 'Table', icon: Table2, href: layoutHref('table'), replace: true, active: view === 'board' && layout === 'table',
          title: 'The board as one table, by tab.' },
        ...(scope.can.provider_calendar ? [{ key: 'calendar', label: 'Provider calendar', icon: CalendarDays, href: viewHref('calendar'), replace: true, onSelect: clearNotice, active: view === 'calendar',
          title: 'A week of visits, one lane per provider. Read-only.' }] : []),
      ],
    },
    {
      key: 'work', label: 'Work',
      items: [
        { key: 'expedited', label: 'Expedited requests', icon: Zap, href: viewHref('expedited'), replace: true, onSelect: clearNotice, active: view === 'expedited',
          badge: expedited ? { value: expedited, label: `${expedited} waiting` } : null },
        ...(atd.available ? [{ key: 'atd', label: 'ATD — ready to dispense', icon: PackageCheck, href: viewHref('atd'), replace: true, onSelect: clearNotice, active: view === 'atd',
          badge: atd.ready ? { value: atd.ready, label: `${atd.ready} ready` } : null,
          // Shown, not only spoken (verification F2): the residents with no plan yet, in the stop tone.
          secondary: atd.notPlanned ? { value: atd.notPlanned, label: `${atd.notPlanned} not planned`, tone: 'stop' as const } : null }] : []),
        // Every building, the board's or not (verification F1, 2026-10-14): always listed, now that the default screen is the
        // Kanban and the table's foot -- where its door was -- is no longer the first thing anyone sees. Same rule as that
        // door: anyone in Scheduling may open it; what they may do there is FacilityOperations' own capability.
        { key: 'facilities', label: 'Facility table', icon: Building2, href: viewHref('facilities'), replace: true, onSelect: clearNotice, active: view === 'facilities',
          title: 'Every building, its last and next visit, who covers it, and what blocks it.' },
      ],
    },
  ];
  return (
    <div className="um board-host">
      <WorkspaceMenu current="SCHEDULING" />
      <WorkspaceLayout workspace="SCHEDULING" navLabel="Scheduling views" groups={sidebarGroups}>
      <header className="um-bar"><div className="um-bar-inner"><div className="um-ident"><div className="wsb-title"><SidebarTrigger /><div className="um-where">
        <span className="um-eyebrow">eMerge · Scheduling</span><h1>{TITLE[view]}</h1>
        <span className="um-sub">{view === 'board'
          ? `Every building's schedule in eMerge: its state, visit plan and coverage, with Monday's columns kept only as reference. ${scope.can.plan ? 'Plan, confirm and complete visits from an open row.' : 'Read-only: the visits write is not open for this account.'}`
          : view === 'facilities'
            ? `Every building, its last and next visit, who covers it, and what blocks it. ${scope.can.plan ? 'Plan, confirm and complete visits here.' : 'Read-only: the visits write is not open for this account.'}`
            : view === 'calendar'
              ? 'Read-only: which facility each provider visits each day, built from eMerge visit plans.'
            : view === 'atd'
              ?'Residents Finance has approved to dispense: put them on a manifest, plan a pit stop, and mark them dispensed.'
              : `Finance asks; Scheduling decides. ${scope.writes === 'NONE' ? 'Read-only on this process.' : ''}`}</span>
      </div></div><div className="um-aside"><FindResidentEntry /></div></div></div></header>
      <main className={`um-main one${view === 'board' ? ' board-main' : ''}`}>
        {notice && (view === 'facilities' || view === 'board') && <p className="um-notice ok" role="status">{notice}</p>}
        {view === 'board' && <SchedulingBoard scope={scope} onNotice={setNotice} onOpenFacilities={() => setView('facilities')} />}
        {view === 'facilities' && <FacilityOperations scope={scope} onNotice={setNotice} />}
        {view === 'atd' && <AtdQueue />}
        {view === 'calendar' && <ProviderCalendar allowed={!!scope.can.provider_calendar} />}
        {view === 'expedited' && <div className="um-list">
          <label className="um-quiet">Show{' '}
            <select value={state} onChange={(e) => setState(e.target.value as ExpeditedState | '')} aria-label="Request state">
              <option value="">Open (requested, under review, accepted)</option>
              {scope.states.map((s) => <option key={s} value={s}>{EXPEDITED_STATE_LABEL[s]}</option>)}
            </select>
          </label>
          {notice && <p className="um-notice ok" role="status">{notice}</p>}
          {listErr && (
            <p className="um-notice stop" role="alert">
              {listErr}{' '}
              <button type="button" className="um-open" onClick={load}>Try again</button>
            </p>
          )}
          {!listErr && items === null && <p className="um-quiet">Loading…</p>}
          {!listErr && items && items.length === 0 && <p className="um-quiet">Nothing waiting.</p>}
          {items && items.length > 0 && <ul className="um-packets" aria-label="Requests">{items.map((it) => <RequestCard key={`${it.id}:${it.version}`} item={it} canRespond={scope.can.respond} onDone={async (m) => { setNotice(m); await load(); }} />)}</ul>}
        </div>}
      </main>
      </WorkspaceLayout>
    </div>
  );
}
